import {
  ChangeDetectionStrategy,
  afterEveryRender,
  booleanAttribute,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { DateAdapter } from '@angular/material/core';
import { MatCalendar, MatCalendarCellClassFunction, MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';

import { provideCalendarDayDateAdapter } from '@core/services/calendar-day-date-adapter';
import { CalendarDay } from '@shared/models/dateTypes';
import { AbookFilterChipComponent } from './abook-filter-chip.component';

/**
 * A day's free times: those booked at once (`free`) and those the administration must approve
 * (`request`). A dot for each kind that has any, the counts in its title.
 */
export interface DayMark {
  free: number;
  request: number;
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** "2026-10-12" → "12-10-2026", the app's DD-MM-ÅÅÅÅ. */
const dayText = (day: CalendarDay) => day.split('-').reverse().join('-');

/**
 * One day as a filter chip (the search variant, #880): "Dato · 12-10-2026". The panel has the
 * DD-MM-ÅÅÅÅ field and the calendar; a pick closes it. `min`/`max` limit both. `dayMarks` puts a dot
 * under a day in the calendar — e.g. free times or requests on Søg/Book (design 3a).
 */
@Component({
  selector: 'abook-date-chip',
  imports: [
    AbookFilterChipComponent,
    OverlayModule,
    ReactiveFormsModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
  ],
  providers: [...provideCalendarDayDateAdapter()],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[class.is-changed]': 'changed()' },
  template: `
    <abook-filter-chip
      #chip
      [icon]="icon()"
      [label]="label()"
      [value]="valueText()"
      [changed]="changed()"
      [required]="required()"
      [requiredText]="requiredText()"
      [invalid]="invalid()"
      [expanded]="open()"
      (opened)="toggle()"
      (cleared)="cleared.emit()"
    />

    <ng-template
      cdkConnectedOverlay
      [cdkConnectedOverlayOrigin]="chip.origin()!"
      [cdkConnectedOverlayOpen]="open() && !!chip.origin()"
      [cdkConnectedOverlayPositions]="positions"
      [cdkConnectedOverlayHasBackdrop]="true"
      cdkConnectedOverlayBackdropClass="cdk-overlay-transparent-backdrop"
      [cdkConnectedOverlayPush]="true"
      (backdropClick)="open.set(false)"
      (overlayKeydown)="$event.key === 'Escape' && open.set(false)"
      (detach)="open.set(false)"
    >
      <div class="abook-filter-panel abook-date-panel" role="dialog" [attr.aria-label]="label()">
        <mat-form-field appearance="outline" subscriptSizing="dynamic">
          <mat-label>{{ label() }}</mat-label>
          <input
            matInput
            [matDatepicker]="picker"
            [formControl]="field"
            [min]="min()"
            [max]="max()"
            placeholder="DD-MM-ÅÅÅÅ"
            (dateChange)="commit()"
          />
          <mat-datepicker #picker />
          <mat-hint>DD-MM-ÅÅÅÅ</mat-hint>
          @if (field.hasError('matDatepickerParse')) {
            <mat-error>Ugyldig dato. Skriv DD-MM-ÅÅÅÅ.</mat-error>
          } @else if (field.hasError('matDatepickerMin')) {
            <mat-error>{{
              minText() === 'i dag' ? 'Datoen ligger før i dag.' : 'Datoen kan ikke ligge før ' + minText()
            }}</mat-error>
          } @else if (field.hasError('matDatepickerMax')) {
            <mat-error>Datoen kan ikke ligge efter {{ maxText() }}</mat-error>
          }
        </mat-form-field>
        <mat-calendar
          [selected]="value()"
          [startAt]="value() ?? min()"
          [minDate]="min()"
          [maxDate]="max()"
          [dateClass]="calendarDayClass"
          (selectedChange)="pick($event)"
        />
        @if (dayMarks()) {
          <div class="abook-date-panel__legend" aria-hidden="true">
            <span><span class="abook-date-panel__dot --free"></span>Direkte booking</span>
            <span><span class="abook-date-panel__dot --request"></span>Kræver godkendelse</span>
          </div>
        }
      </div>
    </ng-template>
  `,
  styles: `
    :host {
      display: contents;
    }
  `,
})
export class AbookDateChipComponent {
  private readonly adapter = inject<DateAdapter<CalendarDay>>(DateAdapter);

  readonly value = input<CalendarDay | null>(null);
  readonly label = input('Dato');
  readonly icon = input('calendar_today');
  readonly min = input<CalendarDay | null>(null);
  readonly max = input<CalendarDay | null>(null);
  readonly changed = input(false, { transform: booleanAttribute });
  readonly required = input(false, { transform: booleanAttribute });
  readonly requiredText = input('Vælg dato');
  readonly invalid = input(false, { transform: booleanAttribute });

  /** Dots under these days: green for free times, blue for requests (DateChip rule 4). */
  readonly dayMarks = input<ReadonlyMap<CalendarDay, DayMark> | null>(null);
  readonly valueChange = output<CalendarDay>();
  readonly cleared = output<void>();
  /** The panel opened — the moment for a page to fetch {@link dayMarks}. */
  readonly panelOpened = output<void>();

  readonly open = signal(false);

  /** mat-calendar asks per cell; read the marks fresh each time so a late answer still shows. */
  protected readonly calendarDayClass: MatCalendarCellClassFunction<CalendarDay> = (day, view) => {
    const mark = view === 'month' ? this.dayMarks()?.get(day) : undefined;
    if (!mark || (!mark.free && !mark.request)) return '';
    const kind = mark.free && mark.request ? 'both' : mark.free ? 'free' : 'request';
    // The counts ride along in a class, for the title set after rendering (mat-calendar has no
    // per-cell title of its own).
    return `abook-day-mark abook-day-mark--${kind} abook-day-n-${mark.free}-${mark.request}`;
  };
  protected readonly field = new FormControl<CalendarDay | null>(null);
  protected readonly positions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 4 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 },
  ];

  protected readonly valueText = computed(() => {
    const value = this.value();
    return value ? dayText(value) : '';
  });
  protected readonly minText = computed(() => {
    const min = this.min();
    return min === this.adapter.today() ? 'i dag' : min ? dayText(min) : '';
  });
  protected readonly maxText = computed(() => {
    const max = this.max();
    return max === this.adapter.today() ? 'i dag' : max ? dayText(max) : '';
  });

  private readonly calendar = viewChild(MatCalendar);

  constructor() {
    // mat-calendar asks for the days' classes when it draws them; marks that arrive after the panel
    // opened (they are fetched on opening) need it to draw the month again.
    effect(() => {
      this.dayMarks();
      this.calendar()?.updateTodaysDate();
    });

    // A marked day says what its dots stand for: "4 ledige tider · 1 kræver godkendelse".
    afterEveryRender(() => {
      if (!this.open() || !this.dayMarks()) return;
      document.querySelectorAll<HTMLElement>('.abook-date-panel .abook-day-mark').forEach((cell) => {
        const counts = /abook-day-n-(\d+)-(\d+)/.exec(cell.className);
        if (!counts) return;
        const [free, request] = [Number(counts[1]), Number(counts[2])];
        const all = free + request;
        cell.title =
          plural(all, 'ledig tid', 'ledige tider') +
          (!request ? '' : request === all ? ' · kræver godkendelse' : ` · ${request} kræver godkendelse`);
      });
    });

    // The page's value is the truth; the field follows it.
    effect(() => {
      const value = this.value();
      if (this.field.value !== value) this.field.setValue(value);
    });
  }

  /** Opens the panel — for a page that asks for the date from elsewhere. */
  openPanel(): void {
    this.open.set(true);
    this.panelOpened.emit();
  }

  protected toggle(): void {
    if (this.open()) this.open.set(false);
    else this.openPanel();
  }

  protected pick(day: CalendarDay | null): void {
    if (!day) return;
    this.field.setValue(day);
    this.valueChange.emit(day);
    this.open.set(false);
  }

  /** A typed date, once it is a valid day within the limits. */
  protected commit(): void {
    const day = this.field.value;
    if (day && this.field.valid && day !== this.value()) this.valueChange.emit(day);
  }
}
